import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiShield, FiClock, FiMessageSquare, FiSend, FiCheck } = FiIcons;

const Join = () => {
  const [formData, setFormData] = useState({
    playerName: '',
    classRole: '',
    timeZone: '',
    discord: '',
    guildGoals: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send email to xonak.official@gmail.com
      await fetch('https://formsubmit.co/ajax/xonak.official@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          playerName: formData.playerName,
          classRole: formData.classRole,
          timeZone: formData.timeZone,
          discord: formData.discord,
          guildGoals: formData.guildGoals,
          _subject: `New Guild Application: ${formData.playerName}`
        })
      });
      
      setIsSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          playerName: '',
          classRole: '',
          timeZone: '',
          discord: '',
          guildGoals: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const values = [
    "We value respect, maturity, and teamwork",
    "Be ready to learn and to teach",
    "IRL and school/work always come first"
  ];

  return (
    <section id="join" className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-red-900/50 via-purple-900/50 to-blue-900/50"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 
            className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-purple-400"
            style={{ fontFamily: 'fantasy, serif' }}
          >
            Join the Crimson Guild
          </h2>
          <p className="text-white text-xl max-w-3xl mx-auto leading-relaxed">
            Tell us a bit about yourself and what you're looking for. Officers reply within 24 hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Guild Values */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-purple-900/80 to-red-900/80 p-8 rounded-2xl shadow-2xl border border-purple-500/30 backdrop-blur-sm">
              <h3 className="text-3xl font-bold text-purple-400 mb-8 text-center">What We Expect</h3>
              
              <div className="space-y-6">
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start space-x-4 p-4 bg-gradient-to-r from-purple-500/20 to-red-500/20 rounded-lg border border-purple-500/30"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <SafeIcon icon={FiCheck} className="text-white text-sm" />
                    </div>
                    <span className="text-white font-medium leading-relaxed">{value}</span>
                  </motion.div>
                ))}
              </div>
              
              <motion.div 
                className="mt-8 p-4 bg-gradient-to-r from-red-500/20 to-purple-500/20 rounded-lg border border-red-500/30"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h4 className="text-xl font-bold text-red-400 mb-2">Join Our Discord</h4>
                <p className="text-white mb-4">Connect with our community and get real-time updates!</p>
                <a 
                  href="https://discord.gg/hHgpWwUg82" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
                >
                  <SafeIcon icon={FiMessageSquare} />
                  <span>Join Discord Server</span>
                </a>
              </motion.div>
            </div>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-blue-900/80 to-emerald-900/80 p-8 rounded-2xl shadow-2xl border border-blue-500/30 backdrop-blur-sm">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h3 className="text-3xl font-bold text-blue-400 mb-8 text-center">Application Form</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center space-x-2 text-white font-semibold mb-2">
                        <SafeIcon icon={FiUser} className="text-blue-400" />
                        <span>Player Name</span>
                      </label>
                      <input
                        type="text"
                        name="playerName"
                        value={formData.playerName}
                        onChange={handleChange}
                        required
                        className="w-full p-4 bg-gradient-to-r from-blue-800/50 to-emerald-800/50 border border-blue-500/50 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:shadow-lg focus:shadow-blue-500/25 transition-all duration-300"
                        placeholder="Enter your character name"
                      />
                    </div>
                    
                    <div>
                      <label className="flex items-center space-x-2 text-white font-semibold mb-2">
                        <SafeIcon icon={FiShield} className="text-emerald-400" />
                        <span>Class / Role</span>
                      </label>
                      <input
                        type="text"
                        name="classRole"
                        value={formData.classRole}
                        onChange={handleChange}
                        required
                        className="w-full p-4 bg-gradient-to-r from-emerald-800/50 to-blue-800/50 border border-emerald-500/50 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-emerald-400 focus:shadow-lg focus:shadow-emerald-500/25 transition-all duration-300"
                        placeholder="e.g., Tank, Healer, DPS"
                      />
                    </div>
                    
                    <div>
                      <label className="flex items-center space-x-2 text-white font-semibold mb-2">
                        <SafeIcon icon={FiClock} className="text-purple-400" />
                        <span>Time Zone</span>
                      </label>
                      <input
                        type="text"
                        name="timeZone"
                        value={formData.timeZone}
                        onChange={handleChange}
                        required
                        className="w-full p-4 bg-gradient-to-r from-purple-800/50 to-blue-800/50 border border-purple-500/50 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-purple-400 focus:shadow-lg focus:shadow-purple-500/25 transition-all duration-300"
                        placeholder="e.g., EST, PST, GMT"
                      />
                    </div>
                    
                    <div>
                      <label className="flex items-center space-x-2 text-white font-semibold mb-2">
                        <SafeIcon icon={FiMessageSquare} className="text-yellow-400" />
                        <span>Discord</span>
                      </label>
                      <input
                        type="text"
                        name="discord"
                        value={formData.discord}
                        onChange={handleChange}
                        required
                        className="w-full p-4 bg-gradient-to-r from-yellow-800/50 to-orange-800/50 border border-yellow-500/50 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-yellow-400 focus:shadow-lg focus:shadow-yellow-500/25 transition-all duration-300"
                        placeholder="YourUsername#1234"
                      />
                    </div>
                    
                    <div>
                      <label className="flex items-center space-x-2 text-white font-semibold mb-2">
                        <SafeIcon icon={FiMessageSquare} className="text-pink-400" />
                        <span>What do you want from a guild?</span>
                      </label>
                      <textarea
                        name="guildGoals"
                        value={formData.guildGoals}
                        onChange={handleChange}
                        required
                        rows="4"
                        className="w-full p-4 bg-gradient-to-r from-pink-800/50 to-purple-800/50 border border-pink-500/50 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-pink-400 focus:shadow-lg focus:shadow-pink-500/25 transition-all duration-300 resize-none"
                        placeholder="Tell us about your goals, playstyle, and what you hope to achieve with our guild..."
                      />
                    </div>
                  </div>
                  
                  <motion.button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-purple-600 hover:from-red-500 hover:to-purple-500 text-white py-4 rounded-xl font-bold text-lg shadow-xl shadow-red-500/30 transition-all duration-300 flex items-center justify-center space-x-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      <>
                        <SafeIcon icon={FiSend} />
                        <span>Submit Application</span>
                      </>
                    )}
                  </motion.button>
                  
                  <p className="text-xs text-center text-gray-300 mt-4">
                    Your application will be sent to our guild leadership team
                  </p>
                </form>
              ) : (
                <motion.div
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/50">
                    <SafeIcon icon={FiCheck} className="text-3xl text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-green-400 mb-4">Application Submitted!</h3>
                  <p className="text-white text-lg">
                    Thank you for your interest in joining Crimson Guild. Our officers will review your application and respond within 24 hours.
                  </p>
                  <p className="text-emerald-300 mt-4">
                    Meanwhile, join our Discord server to connect with the community!
                  </p>
                  <a 
                    href="https://discord.gg/hHgpWwUg82" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 hover:scale-105 mt-4"
                  >
                    <SafeIcon icon={FiMessageSquare} />
                    <span>Join Discord Server</span>
                  </a>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Join;